import { Check, CheckCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  itemStockStatus,
  itemStockTone,
  type ItemStockRow,
} from "@/features/staff/item-stock/item-stock"
import {
  confirmAllStockDrafts,
  confirmStockDraft,
  isValidStockValue,
  pendingStockDrafts,
  stockDraftState,
  tTill,
  withStockValue,
  type StockDraft,
} from "@/features/staff/till"
import { toneDotClass } from "@/lib/status-colors"

/**
 * The opening arqueo: how many of each counted dish the day starts with.
 *
 * ## Two signals, two places
 *
 * The dot before the name is the DISH's condition — agotado, se está acabando,
 * disponible — and mirrors `Location::ItemStock#depleted?` / `#low?` through
 * `itemStockTone`. The control on the right is the CASHIER's decision about it:
 * pending until they resolve it, then Confirmado or Editado. Collapsing the two
 * into one column was the easy way out and the wrong one — a dish can be sold
 * out and confirmed, and that row has to read as settled, not as an alarm.
 *
 * ## Confirming is accepting what is already there
 *
 * Every field arrives holding the number the last close left behind, so the
 * common case is a glance and a tap — and "Confirmar todo igual" is the whole
 * list in one. Typing a different number resolves the row by itself: an edit is
 * already a decision, and asking someone to confirm what they just typed is
 * ceremony. Obligatory does not have to mean slow, and at eight in the morning
 * that difference decides whether the feature gets used or resented.
 *
 * ## The rows are the drafts
 *
 * Rendering `stocks` and looking each draft up meant a stock with no draft
 * rendered as `null`: a dish silently missing from a list the cashier is about
 * to sign, with the form still reporting itself complete. The drafts are the
 * form's own state and the only thing the payload is built from, so they are
 * what the list shows. Keeping them in step with `stocks` is the parent's job
 * -- see `TillOpenForm`.
 *
 * ## The blank field is not a zero
 *
 * A cleared field un-resolves its row and blocks the open. `Number("")` is 0 in
 * JavaScript, and a dish taken off the menu because someone was mid-keystroke
 * is the exact failure this screen exists to prevent.
 */

type TillStockCountProps = {
  stocks: ItemStockRow[]
  drafts: StockDraft[]
  onChange: (drafts: StockDraft[]) => void
}

export function TillStockCount({ stocks, drafts, onChange }: TillStockCountProps) {
  const pending = pendingStockDrafts(drafts)
  const stockById = new Map(stocks.map((stock) => [ stock.id, stock ]))

  if (stocks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="shift-stock-empty">
        {tTill("stock_empty")}
      </p>
    )
  }

  return (
    <section className="space-y-3" data-testid="shift-stock-count">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{tTill("stock_title")}</h3>
          <p className="text-xs text-muted-foreground" data-testid="shift-stock-progress">
            {tTill("stock_progress", { confirmed: drafts.length - pending, total: drafts.length })}
          </p>
        </div>

        {pending > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(confirmAllStockDrafts(drafts))}
            data-testid="shift-stock-confirm-all"
          >
            <CheckCheck className="size-4" />
            {tTill("stock_confirm_all")}
          </Button>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground">{tTill("stock_hint")}</p>

      <ul className="divide-y rounded-md border">
        {drafts.map((draft) => {
          const stock = stockById.get(draft.itemStockId)
          const state = stockDraftState(draft)

          return (
            <li
              key={draft.itemStockId}
              className="flex items-center gap-3 px-4 py-2.5"
              data-testid={`shift-stock-row-${draft.itemStockId}`}
            >
              {/* The dish's own condition, when the row it came from is still
                  on screen. A draft that outlived its stock keeps its line and
                  loses only the colour. */}
              <span
                aria-hidden="true"
                className={toneDotClass(stock ? itemStockTone(itemStockStatus(stock)) : "disabled")}
              />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{draft.itemName}</p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {tTill("stock_closing_quantity", { count: draft.closingQuantity })}
                </p>
              </div>

              <Input
                className="w-20 tabular-nums"
                inputMode="numeric"
                autoComplete="off"
                aria-label={tTill("stock_quantity_label", { name: draft.itemName })}
                aria-invalid={!isValidStockValue(draft.value)}
                value={draft.value}
                onChange={(event) =>
                  onChange(withStockValue(drafts, draft.itemStockId, event.target.value))
                }
                data-testid={`shift-stock-quantity-${draft.itemStockId}`}
              />

              {state === "pending" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={!isValidStockValue(draft.value)}
                  aria-label={tTill("stock_confirm_row")}
                  onClick={() => onChange(confirmStockDraft(drafts, draft.itemStockId))}
                  data-testid={`shift-stock-confirm-${draft.itemStockId}`}
                >
                  <Check className="size-4" />
                </Button>
              ) : (
                <StatusBadge
                  tone={state === "edited" ? "reserved" : "available"}
                  label={tTill(`stock_state_${state}`)}
                  data-testid={`shift-stock-state-${draft.itemStockId}`}
                />
              )}
            </li>
          )
        })}
      </ul>

      <p className="text-xs text-muted-foreground">{tTill("stock_zero_hint")}</p>
    </section>
  )
}
